import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ClipColorStudio from '../components/ClipColorStudio';
import FrameGrabber from '../components/FrameGrabber';
import { useAsset, updateAssetGrade, updateAssetCrop, updateClipKeyframes, updateClipReframeKeys, requestClipGradeRender } from '../data/firestore';
import { getAssetPlaybackUrl } from '../data/uploadService';
import { httpsify } from '../utils/url';
import { DEFAULT_GRADE, RED_DEFAULT_GRADE, clone, gradeSignature } from '../utils/grade';
import { assetRenderJob } from '../utils/eta';

const IDENTITY = gradeSignature(DEFAULT_GRADE);

// Standalone color/light editor for a single uploaded clip. The look is saved
// onto the asset (`asset.grade`) so it can be carried into any movie that uses
// this footage, or re-edited later from raw. Photos use the dedicated RAW
// PhotoEditor instead.
export default function EditClip() {
  const { tripId, assetId } = useParams();
  const navigate = useNavigate();
  const { doc: asset } = useAsset(tripId, assetId);

  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  const [grade, setGrade] = useState(() => clone(DEFAULT_GRADE));
  const [savedAt, setSavedAt] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [grabbing, setGrabbing] = useState(false);
  const [keyframes, setKeyframes] = useState([]);
  const [segment, setSegment] = useState(null);
  const [crop, setCrop] = useState(null);
  const [reframeKeys, setReframeKeys] = useState([]);
  const hydrated = useRef(false);
  const kfHydrated = useRef(false);
  const cropHydrated = useRef(false);
  const saveTimer = useRef(null);
  const kfTimer = useRef(null);
  const cropTimer = useRef(null);
  const reframeTimer = useRef(null);

  const isRed = !!asset?.isRawVideo;
  const proxyReady = asset?.proxy?.status === 'ready' && !!asset?.proxyHlsUrl;
  const proxyUrl = proxyReady ? httpsify(asset.proxyHlsUrl) : null;
  const s3Key = asset?.s3Key;

  // Resolve the playback URL (RED → CDN proxy, raw video → presigned MP4).
  useEffect(() => {
    let alive = true;
    setUrl(null);
    setErr(null);
    if (isRed) {
      if (proxyUrl) setUrl(proxyUrl);
    } else if (s3Key) {
      getAssetPlaybackUrl(s3Key)
        .then((u) => alive && setUrl(u))
        .catch((e) => alive && setErr(e.message));
    }
    return () => { alive = false; };
  }, [isRed, proxyUrl, s3Key]);

  const refreshSrc = useCallback(
    () => (!isRed && s3Key ? getAssetPlaybackUrl(s3Key) : Promise.resolve(null)),
    [isRed, s3Key]
  );

  // Hydrate the grade once from the asset. RED footage decodes flat
  // (Log3G10 / RED Wide Gamut), so when a RED clip has no saved look yet we seed
  // — and persist — the IPP2 de-flatten baseline. The clip then looks natural by
  // default everywhere it's used (movies, frame grabs) and stays fully editable
  // or clearable from here.
  useEffect(() => {
    if (asset && !hydrated.current) {
      hydrated.current = true;
      if (asset.grade) {
        setGrade(clone(asset.grade));
      } else if (asset.isRawVideo) {
        const seed = clone(RED_DEFAULT_GRADE);
        setGrade(seed);
        updateAssetGrade(tripId, assetId, seed)
          .then(() => setSavedAt(Date.now()))
          .catch(() => {});
      }
    }
  }, [asset, tripId, assetId]);

  // Hydrate keyframes/segment once from the asset.
  useEffect(() => {
    if (asset && !kfHydrated.current) {
      kfHydrated.current = true;
      if (Array.isArray(asset.gradeKeyframes)) setKeyframes(asset.gradeKeyframes);
      if (asset.gradeSegment) setSegment(asset.gradeSegment);
    }
  }, [asset]);

  // Hydrate the non-destructive reframe/zoom (static + animated) once.
  useEffect(() => {
    if (asset && !cropHydrated.current) {
      cropHydrated.current = true;
      if (asset.crop && asset.crop.w > 0) setCrop(asset.crop);
      if (Array.isArray(asset.reframeKeys)) setReframeKeys(asset.reframeKeys);
    }
  }, [asset]);

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (kfTimer.current) clearTimeout(kfTimer.current);
    if (cropTimer.current) clearTimeout(cropTimer.current);
    if (reframeTimer.current) clearTimeout(reframeTimer.current);
  }, []);

  // Persist the static reframe/zoom (debounced); the clip render bakes it in.
  const onCropChange = (next) => {
    setCrop(next || null);
    setDirty(true);
    if (cropTimer.current) clearTimeout(cropTimer.current);
    cropTimer.current = setTimeout(() => {
      updateAssetCrop(tripId, assetId, next || null)
        .then(() => { setSavedAt(Date.now()); setDirty(false); })
        .catch(() => {});
    }, 600);
  };

  // Persist the animated reframe ("keyframed zoom"), debounced.
  const onReframeChange = (nextKeys) => {
    setReframeKeys(nextKeys || []);
    setDirty(true);
    if (reframeTimer.current) clearTimeout(reframeTimer.current);
    reframeTimer.current = setTimeout(() => {
      updateClipReframeKeys(tripId, assetId, nextKeys || [])
        .then(() => { setSavedAt(Date.now()); setDirty(false); })
        .catch(() => {});
    }, 600);
  };

  // Persist keyframes/segment (debounced) and reflect immediately in the UI.
  const onKeyframesChange = ({ keyframes: nextKfs, segment: nextSeg }) => {
    setKeyframes(nextKfs);
    setSegment(nextSeg || null);
    setDirty(true);
    if (kfTimer.current) clearTimeout(kfTimer.current);
    kfTimer.current = setTimeout(() => {
      updateClipKeyframes(tripId, assetId, { keyframes: nextKfs, segment: nextSeg })
        .then(() => { setSavedAt(Date.now()); setDirty(false); })
        .catch(() => {});
    }, 700);
  };

  const onRequestRender = (output) => {
    // Flush any pending keyframe / crop / reframe saves so the worker reads the latest.
    if (kfTimer.current) clearTimeout(kfTimer.current);
    if (cropTimer.current) clearTimeout(cropTimer.current);
    if (reframeTimer.current) clearTimeout(reframeTimer.current);
    Promise.all([
      updateClipKeyframes(tripId, assetId, { keyframes, segment }),
      updateAssetCrop(tripId, assetId, crop || null),
      updateClipReframeKeys(tripId, assetId, reframeKeys || []),
    ])
      .then(() => requestClipGradeRender(tripId, assetId, output))
      .catch(() => {});
  };

  const onChange = (next) => {
    setGrade(next);
    setDirty(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const toSave = gradeSignature(next) === IDENTITY ? null : next;
      updateAssetGrade(tripId, assetId, toSave)
        .then(() => { setSavedAt(Date.now()); setDirty(false); })
        .catch(() => {});
    }, 700);
  };

  const clearLook = () => onChange(clone(DEFAULT_GRADE));

  if (asset === undefined) return <div className="cs-page">Loading…</div>;
  if (asset === null) return <div className="cs-page">Footage not found.</div>;
  if (asset.kind === 'photo') {
    return (
      <div className="cs-page">
        <div className="cs-empty">
          This is a photo — use the RAW photo editor instead.
          <div style={{ marginTop: 12 }}>
            <button className="cs-btn cs-btn-primary" onClick={() => navigate(`/photo/${tripId}/${assetId}`)}>
              Open photo editor
            </button>
          </div>
        </div>
      </div>
    );
  }

  const redPending = isRed && !proxyReady;
  const hasLook = gradeSignature(grade) !== IDENTITY;

  return (
    <div className="cs-page cs-edit-page cs-edit-locked">
      <div className="cs-editbar cs-editbar-wrap">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => navigate(`/watch/asset/${tripId}/${assetId}`)}>←</button>
          <h1>Color &amp; Light · {asset.name}</h1>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {!redPending && url && (
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={() => setGrabbing(true)}
              title="Save a full-resolution still with the look you're editing"
            >
              🖼 Grab frame
            </button>
          )}
          <button
            className="cs-btn cs-btn-ghost cs-btn-sm"
            onClick={() => navigate(`/clip-grade/${tripId}/${assetId}`)}
            title="Open the per-clip grading session: review proofs, give feedback, then bake every frame"
          >
            🎚 Grade session
          </button>
          {hasLook && (
            <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={clearLook} title="Remove the saved look (back to raw)">
              Clear look
            </button>
          )}
          <span className="cs-help" style={{ fontSize: 12 }}>
            {dirty ? 'Saving…' : savedAt ? 'Saved to clip ✓' : hasLook ? 'Look saved to clip' : 'Auto-saves to this clip'}
          </span>
        </div>
      </div>

      <div className="cs-help" style={{ maxWidth: 1100, margin: '0 auto 12px', padding: '0 4px' }}>
        Grade this clip on its own — the look is saved to the source. When you add this footage to a
        movie it comes in with your look applied; you can toggle back to <strong>Raw</strong> per clip
        in the movie editor, or return here to re-edit.
      </div>

      {err ? (
        <div className="cs-page"><div className="cs-empty">Couldn’t load this clip: {err}</div></div>
      ) : redPending ? (
        <div className="cs-page">
          <div className="cs-empty">
            {asset.proxy?.status === 'error'
              ? `Stream proxy failed: ${asset.proxy?.message || 'unknown error'}.`
              : 'Decoding the RED stream proxy — grading will be available once it’s ready.'}
          </div>
        </div>
      ) : !url ? (
        <div className="cs-page"><div className="cs-empty">Preparing the clip…</div></div>
      ) : (
        <ClipColorStudio
          url={url}
          type={isRed ? 'hls' : 'mp4'}
          grade={grade}
          onChange={onChange}
          getFreshSrc={refreshSrc}
          keyframes={keyframes}
          segment={segment}
          onKeyframesChange={onKeyframesChange}
          onRequestRender={onRequestRender}
          renderJob={assetRenderJob(asset)}
          clipResult={asset?.clipGrade || null}
          crop={crop}
          onCropChange={onCropChange}
          reframeKeys={reframeKeys}
          onReframeChange={onReframeChange}
        />
      )}

      {grabbing && (
        <FrameGrabber
          asset={asset}
          tripId={tripId}
          gradeOverride={hasLook ? grade : null}
          onClose={() => setGrabbing(false)}
        />
      )}
    </div>
  );
}
